import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import { useActivityShortlists } from "PFCore/hooks/queries/shortlists/use_activity_shortlists";
import { useShortlistCreate } from "PFCore/hooks/queries/shortlists/use_shortlist_create";
import { Dispatch, SetStateAction, useEffect } from "react";

import { useActivityTemplateType } from "../../../../activities/hooks";
import { useBookingActivityContext } from "../../../parts/providers/booking_activity_context_provider";
import { BookingFormData, BookingFormDataCreateMode, BookingFormMode } from "../booking_form.types";
import { isEditMode } from "../booking_form.utils";
import { BookingFormValues } from "../booking_form_provider";
import { isGroupOption } from "../workforce_member_select/workforce_member_select.utils";

const SHORTLISTS_LOADING = "shortlists-loading";

type UsePreSubmitActions = {
  initialData: BookingFormData;
  mode: BookingFormMode;
  setLoadingState: Dispatch<SetStateAction<Record<string, boolean>>>;
  onPreSubmitAction?: (shortlistId: number | undefined) => Promise<void>;
};
type PreSubmitActionsFunction = (values: BookingFormValues) => Promise<number | undefined>;
type UsePreSubmitActionsReturn = {
  preSubmitActions: PreSubmitActionsFunction;
};

export const usePreSubmitActions = ({
  initialData,
  mode,
  setLoadingState,
  onPreSubmitAction
}: UsePreSubmitActions): UsePreSubmitActionsReturn => {
  const { activity } = useBookingActivityContext();
  const { isBookingResourcer } = useBookingModulePermissions();
  const { isRole: isRoleRelated } = useActivityTemplateType(activity);
  const { data: activityShortlists, isFetching } = useActivityShortlists(activity?.id ?? -1, undefined, {
    enabled: !!activity?.id && isRoleRelated && isBookingResourcer
  });
  const { mutateAsync: createShortlist } = useShortlistCreate();

  useEffect(() => {
    if (isFetching) {
      setLoadingState((prev) => ({ ...prev, [SHORTLISTS_LOADING]: true }));
    } else {
      setLoadingState((prev) => ({ ...prev, [SHORTLISTS_LOADING]: false }));
    }
  }, [isFetching]);

  const shortlistProfileIfNeeded: PreSubmitActionsFunction = async (values) => {
    const { workforceMember, activityId } = values;
    const isGroupBooking = isGroupOption(workforceMember);
    if (
      !isBookingResourcer ||
      isEditMode(mode) ||
      !isRoleRelated ||
      !activityId ||
      !!isGroupBooking ||
      !workforceMember?.id
    ) {
      return undefined;
    }
    const shortlist = activityShortlists?.entries.find(({ profile }) => profile.id === workforceMember.id);
    if (shortlist) {
      return shortlist.id;
    }
    return (
      await createShortlist({
        activityId,
        profileId: workforceMember.id,
        named: !(initialData as BookingFormDataCreateMode).isProfileMatched
      })
    ).id;
  };

  const preSubmitActions: PreSubmitActionsFunction = async (values) => {
    const shortlistId = await shortlistProfileIfNeeded(values);
    await onPreSubmitAction?.(shortlistId);
    return shortlistId;
  };

  return {
    preSubmitActions
  };
};
